import { LIMITS } from '@crew/shared';
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { formatWhen } from '@/lib/activity';
import { LockIcon, SendIcon } from '../icons';
import { Spinner } from '../ui/Spinner';

export interface ChatLine {
  id: string;
  /** הודעה "שלנו" — מוצגת בצד השני של המסך */
  mine: boolean;
  authorName: string;
  text: string;
  at: Date | null;
}

/**
 * תצוגת שיחה משותפת: למנהלים (שיחה עם נרשם / צ'אט המנהלים) ולנרשם בדף האישי.
 * Enter שולח, Shift+Enter שורה חדשה (בטלפון Enter תמיד שורה חדשה — שולחים בכפתור).
 */
export function ChatView({
  lines,
  loading,
  error,
  closed,
  closedText = 'השיחה סגורה לכתיבה.',
  emptyText,
  showAuthors = true,
  onSend,
  height = 'h-[58vh] md:h-[62vh]',
  placeholder = 'כתיבת הודעה…',
}: {
  lines: ChatLine[];
  loading?: boolean;
  error?: ReactNode;
  closed?: boolean;
  closedText?: string;
  emptyText: string;
  showAuthors?: boolean;
  onSend: (text: string) => Promise<boolean>;
  height?: string;
  placeholder?: string;
}) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const lastId = lines[lines.length - 1]?.id;

  // גלילה להודעה האחרונה כשמגיעה הודעה חדשה
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lastId, loading]);

  async function send() {
    const v = text.trim();
    if (!v || sending) return;
    setSending(true);
    const ok = await onSend(v);
    setSending(false);
    if (ok) setText('');
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    const touch = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
    if (e.key === 'Enter' && !e.shiftKey && !touch && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send();
    }
  }

  return (
    <div className="flex flex-col overflow-hidden rounded-card border border-line bg-surface">
      <div ref={listRef} className={`${height} space-y-2 overflow-y-auto bg-paper px-3 py-4`} aria-live="polite">
        {loading && (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        )}
        {!loading && error}
        {!loading && !error && lines.length === 0 && <p className="py-10 text-center text-[14px] text-ink-3">{emptyText}</p>}
        {!loading &&
          lines.map((m, i) => {
            const prev = lines[i - 1];
            const grouped = prev && prev.mine === m.mine && prev.authorName === m.authorName;
            return (
              <div key={m.id} className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[82%] rounded-[14px] px-3 py-2 shadow-sm ${
                    m.mine ? 'rounded-ee-[4px] bg-brand text-white' : 'rounded-es-[4px] border border-line bg-surface text-ink'
                  }`}
                >
                  {showAuthors && !grouped && m.authorName && (
                    <div className={`mb-0.5 text-[12px] font-semibold ${m.mine ? 'text-steel' : 'text-accent'}`}>{m.authorName}</div>
                  )}
                  <p className="whitespace-pre-wrap break-words text-[15px] leading-relaxed">{m.text}</p>
                  {m.at && <div className={`mt-0.5 text-end text-[11px] ${m.mine ? 'text-steel' : 'text-ink-3'}`}>{formatWhen(m.at)}</div>}
                </div>
              </div>
            );
          })}
      </div>

      {closed ? (
        <div className="flex items-center gap-2 border-t border-line bg-sunken px-4 py-3 text-[14px] text-ink-2">
          <LockIcon size={17} />
          {closedText}
        </div>
      ) : (
        <div className="flex items-end gap-2 border-t border-line p-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            rows={1}
            maxLength={LIMITS.maxMessageLength}
            placeholder={placeholder}
            aria-label="הודעה"
            className="max-h-32 min-h-11 flex-1 resize-none rounded-control border border-line-strong bg-surface px-3 py-2.5 text-[16px] leading-snug focus:border-brand focus:outline-none"
            style={{ fieldSizing: 'content' } as React.CSSProperties}
          />
          <button
            type="button"
            onClick={send}
            disabled={!text.trim() || sending}
            aria-label="שליחה"
            className="flex size-11 shrink-0 items-center justify-center rounded-full bg-accent text-white transition-opacity disabled:opacity-40"
          >
            {sending ? <Spinner size={18} tone="light" /> : <SendIcon size={20} />}
          </button>
        </div>
      )}
    </div>
  );
}
